import { Injectable } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter, Subscription } from "rxjs";

type ConsentState = "granted" | "denied";

declare global {
  interface Window {
    clarity?: (...args: any[]) => void;
    gtag?: (...args: any[]) => void;
  }
}

type ClarityNativeApi = {
  init: (projectId: string) => void;
  event: (eventName: string) => void;
  setTag: (key: string, value: string | string[]) => void;
  identify: (
    customerId: string,
    customSessionId?: string,
    customPageId?: string,
    friendlyName?: string
  ) => void;
  consentV2: (options?: { ad_Storage: ConsentState; analytics_Storage: ConsentState }) => void;
};

/** Clarity custom tag key for lifecycle segment (matches HotJar / chat `user_segment`). */
const USER_SEGMENT_TAG = "user_segment";

@Injectable({
  providedIn: "root",
})
export class ClarityService {
  private started = false;
  /** Prevents overlapping dynamic imports before `started` flips true */
  private initRequested = false;
  private routeTrackingSub?: Subscription;
  private native?: ClarityNativeApi;
  private lastIdentifiedUser?: string;
  private lastUserSegment?: string;

  /** Snippet + queue API from index.html; prefer over npm `native` so events enqueue before the remote tag executes */
  private invokeClarity(...args: unknown[]): boolean {
    if (typeof window === "undefined") return false;
    const fn = window.clarity;
    if (typeof fn !== "function") {
      window.clarity("stop");
      return false;
    }
    (fn as (...a: unknown[]) => void)(...args);
    return true;
  }

  init(projectId: string): void {
    if (this.initRequested || typeof window === "undefined" || !projectId) return;
    this.initRequested = true;

    import("@microsoft/clarity")
      .then((module) => {
        const clarity = module?.default as ClarityNativeApi | undefined;
        if (!clarity?.init) {
          return;
        }
        // Stop any CDN-injected Clarity instance (e.g. via GTM) before initialising
        // the npm package. An older CDN version (e.g. 0.8.65) contains a null-safety
        // bug in its internal Sr() serialisation routine that throws
        // "Cannot read properties of null (reading 'sequence')".
        // Stopping it here ensures the npm package (^1.0.2) is the sole running instance.
        clarity.init(projectId);
        this.native = clarity;
        // Delay to stabilize session
        setTimeout(() => {
          this.started = true;

          this.applyConsentV2("granted", "granted");

        }, 1000);
      })
      .catch((err) => {
       return err as Error;
      });
  }

  event(name: string): void {
    if (!name) return;

    const maxAttempts = 20;
    let attempts = 0;

    const sendEvent = () => {
      if (this.invokeClarity("event", name)) {
        return;
      }

      if (this.native?.event) {
        this.native.event(name);
        return;
      }

      if (attempts++ < maxAttempts) {
        setTimeout(sendEvent, 500);
      }
    };

    // Delay ensures session is ready
    setTimeout(sendEvent, 1500);
  }

  /** Non-empty string suitable for Clarity tags or custom-id (excludes null/undefined literals). */
  private isValidTagValue(value: unknown): value is string {
    if (value === null || value === undefined) return false;
    const normalized = String(value).trim();
    return normalized.length > 0 && normalized !== "null" && normalized !== "undefined";
  }

  setTag(key: string, value: string | string[]): void {
    if (!key || value === undefined || value === null) return;

    if (this.invokeClarity("set", key, value)) {
      return;
    }
    if (this.native) {
      this.native.setTag(key, value);
    }
  }

  setUserSegment(segment: string | null | undefined): void {
    if (!this.isValidTagValue(segment)) return;

    const normalized = String(segment).trim();
    if (this.lastUserSegment === normalized) return;
    this.lastUserSegment = normalized;
    this.setTag(USER_SEGMENT_TAG, normalized);
  }

  identify(pwaId: string, pageId?: string): void {
    if (!this.isValidTagValue(pwaId)) return;
    // Call the Clarity native identify method
    const identifyKey = pageId ? `${pwaId}:${pageId}` : pwaId;
    const identified = this.invokeClarity("identify", pwaId, undefined, pageId, pwaId);
    if (!identified && this.native) {
      this.native.identify(pwaId, undefined, pageId, pwaId);
    }
    if (identified || this.native) {
      this.lastIdentifiedUser = identifyKey;
    }
    // Set the auth state tag
    this.setTag("auth_state", "authenticated");
  }

  syncAuthenticatedUser(pwaId: string | null | undefined, segment?: string | null | undefined): void {
    if (this.isValidTagValue(pwaId)) {
      this.identify(pwaId);
    }
    this.setUserSegment(segment);
  }

  clearAuthenticatedClarityContext(): void {
    this.lastIdentifiedUser = undefined;
    this.lastUserSegment = undefined;
  }

  setGa4UserId(userId: string | null): void {
    window?.gtag?.("set", { user_id: userId });
  }

  ga4Event(name: string, params: Record<string, unknown> = {}): void {
    if (!name) return;
    window?.gtag?.("event", name, params);
  }

  applyConsentV2(adStorage: ConsentState, analyticsStorage: ConsentState): void {
    const opts = {
      ad_Storage: adStorage,
      analytics_Storage: analyticsStorage,
    };
    if (this.invokeClarity("consentv2", opts)) {
      return;
    }
    if (this.native) {
      this.native.consentV2(opts);
    }
  }

  bindRouteTracking(
    router: Router,
    getMemberId?: () => string | undefined,
    getLocale?: () => string | undefined,
    getCurrency?: () => string | undefined,
    getUserSegment?: () => string | null | undefined
  ): void {
    this.routeTrackingSub?.unsubscribe();
    this.routeTrackingSub = router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        // Delay helps Clarity session sync
        setTimeout(() => {
          const cleanPath = event.urlAfterRedirects.split("?")[0] || "/";
          const memberId = getMemberId?.();
          const isAuthed = !!memberId && memberId !== "null";

          this.setTag("page_path", cleanPath);
          this.setTag("site_area", isAuthed ? "logged_in_app" : "logged_out_main");
          this.setTag("journey_step", this.mapJourneyStep(cleanPath));
          this.setTag("auth_state", isAuthed ? "authenticated" : "anonymous");

          const locale = getLocale?.();
          if (locale) this.setTag("locale", locale);

          const currency = getCurrency?.();
          if (currency) this.setTag("currency", currency);

          this.ga4Event("page_view", {
            page_path: cleanPath,
            page_location: window.location.href,
          });

          if (isAuthed) {
            this.identify(memberId, cleanPath);
            const segment = getUserSegment?.();
            this.setUserSegment(segment);
          }
        }, 500);
      });
  }

  private mapJourneyStep(path: string): string {
    if (path.includes("checkout")) return "checkout";
    if (path.includes("login")) return "login";
    if (path.includes("help") || path.includes("support")) return "help";
    if (path.includes("purchase") || path.includes("payment")) return "purchase";
    if (path.includes("home")) return "home";
    return "landing";
  }
}
